import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  Inject,
  ViewChild,
  ChangeDetectionStrategy,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SortDirection } from '@angular/material/sort';
import { ActivatedRoute, Params, Router } from '@angular/router';

import {
  ISnFeedQueryParameters,
  ISnListQueryParameters,
  ISnListResponse,
  SortParameters,
} from '@lib/api';
import { SnCameraGrid } from '@lib/api/entities';
import { SnCameraGridsAPI } from '@lib/api/services';
import { SnDataListComponent } from '@lib/ui/data-list';

@Component({
  selector: 'sn-camera-grids-dialog',
  templateUrl: './camera-grids-dialog.component.html',
  styleUrls: ['./camera-grids-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CameraGridsDialogComponent implements OnInit, OnDestroy {
  @ViewChild('list', { static: false })
  public readonly list: SnDataListComponent<SnCameraGrid>;

  public sort: string = 'name';
  public sortDirection: SortDirection = 'asc';
  public readonly loadData = this._loadData.bind(this);
  public readonly feedData = this._feedData.bind(this);
  public readonly columns: string[] = ['name', 'geometry', 'cameras', 'owner'];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly _dialogRef: MatDialogRef<CameraGridsDialogComponent>,
    private readonly _cameraGridsAPI: SnCameraGridsAPI,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
  ) {}

  public ngOnInit(): void {
    setTimeout(() => {
      this.list.reference.reload();
    }, 500);
  }

  public ngOnDestroy(): void {
    this._router.navigate([], { queryParams: {} });
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSearchValueChange(value: string): void {
    this._updateQueryParams({ search: value });
  }

  public onSelected(cameraGrid: SnCameraGrid): void {
    this._dialogRef.close(cameraGrid);
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  private _updateQueryParams(params: Params): void {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParamsHandling: 'merge',
      queryParams: params,
    });
  }

  private _loadData(params: ISnListQueryParameters): Observable<ISnListResponse<SnCameraGrid>> {
    return this._cameraGridsAPI
      .getCameraGridList(this._prepareLoadQueryParameters(params))
      .pipe(takeUntil(this._destroy$));
  }

  private _feedData(params: ISnFeedQueryParameters): Observable<SnCameraGrid[]> {
    return this._cameraGridsAPI
      .feedCameraGrid(this._prepareLoadQueryParameters(params))
      .pipe(takeUntil(this._destroy$));
  }

  private _prepareLoadQueryParameters<T extends ISnListQueryParameters>(params: T): T {
    return {
      ordering: new SortParameters({
        active: this.sort,
        direction: this.sortDirection,
      }).convertedActive,
      ...params,
    };
  }
}
